import { gql } from "@apollo/client";
import { FaustTemplate } from "@faustwp/core";
import { SEO } from "@components";
import { GetPageDataQuery } from "@graphqlTypes";
import FlexibleContentMap from "@/components/ComponentMaps/FlexibleContentMap";
import { FlexiblePageContentFragment } from "@/fragments/FlexibleContent";
import { usePageHighlight } from "@/providers/PageHighlightContext";
import { HIGHLIGHT_COLOR_BY_TYPE, HIGHLIGHT_COLORS } from "@/core/constants";
import { useEffect } from "react";
import { getBasicQueryVars, getPageTitle } from "@/core/utils";
import { PageDataFragment } from "@/fragments/PageData";
import { usePageLoading } from "@/providers/LoadingContext";
import { useTrackRouteChange } from "@/providers/TrackingContext";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import { useRouter } from "next/router";
import { getStringParam } from "@/utils/urlParams";

const Page: FaustTemplate<GetPageDataQuery> = ({ pageWrapperData, data, loading, __SEED_NODE__: seedNode }: FaustPageProps<GetPageDataQuery>) => {
  const { siteTitle } = usePageWrapperData();
  const { recentStories } = pageWrapperData;
  const { setHighlightColor } = usePageHighlight();
  const { title, flexiblePageContent, pageData: { pageCategory, ogTitle, ogDescription, featuredImage } = {} } = data?.rhPage || {};
  const seoTitle = getPageTitle(siteTitle, ogTitle || title);
  const { previewLoading, setPreviewLoading } = usePageLoading();
  const router = useRouter();
  const [category] = pageCategory || [];

  useEffect(() => {
    setHighlightColor(HIGHLIGHT_COLOR_BY_TYPE[category?.slug] || HIGHLIGHT_COLORS.GREEN);
  }, [category]);
  useTrackRouteChange(category?.slug);

  useEffect(() => {
    if (!loading && previewLoading) setPreviewLoading(false);
  }, [loading, previewLoading]);

  const handleSetParams = () => {
    const grantUrl = getStringParam('base_grant_url', '');
    const continueUrl = getStringParam('user_continue_url', '');
    localStorage.setItem('base_grant_url', grantUrl);
    localStorage.setItem('user_continue_url', continueUrl);
  }

  useEffect(() => {
    const isWifiForm = seedNode?.template.templateName === "Wifi Form";
    if (isWifiForm && router.isReady) handleSetParams();
  }, [router.isReady]);

  if (loading) return;

  return (
    <>
      <SEO
        title={seoTitle}
        description={ogDescription || ""}
        image={featuredImage}
      />
      <FlexibleContentMap
        content={flexiblePageContent?.content}
        recentStories={recentStories}
      />
    </>
  );
};

Page.variables = getBasicQueryVars;

Page.query = gql`
  ${PageDataFragment}
  ${FlexiblePageContentFragment}
  query GetPageData($databaseId: ID!, $asPreview: Boolean = false, $language: String = "en") {
    rhPage(id: $databaseId, asPreview: $asPreview, language: $language) {
      title
      ...PageData
      flexiblePageContent {
        ...ContentFragment
      }
    }
  }
`;

export default Page;
